import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import { createEntityAdapter } from '@reduxjs/toolkit';
import chronosApi from '@chronos/api-client/chronosApi';
import { trelloBaseApi } from './trelloBaseApi';

export const cardsAdapter = createEntityAdapter();
export const initialCardState = cardsAdapter.getInitialState();
const labelsAdapter = createEntityAdapter();
export const initialLabelState = labelsAdapter.getInitialState();
const listsAdapter = createEntityAdapter();
export const initialListState = listsAdapter.getInitialState();

const apiWithTag = trelloBaseApi.enhanceEndpoints({
  addTagTypes: ['Cards'],
});
const cardsApi = apiWithTag.injectEndpoints({
  endpoints: (build) => ({
    getTrelloBoardCards: build.query({
      query: ({ boardId, filter, fields }) => ({
        url: `/boards/${boardId}/cards/${filter}`,
        method: 'GET',
        params: {
          fields,
        },
      }),
      transformResponse: (responseData) => {
        return cardsAdapter.setAll(initialCardState, responseData);
      },
    }),
    getTrelloCard: build.query({
      query: ({ cardId }) => ({
        url: `/cards/${cardId}`,
        method: 'GET',
      }),
      transformResponse: (responseData) => {
        return cardsAdapter.setOne(initialCardState, responseData);
      },
    }),
    getTrelloBoardLabels: build.query({
      query: ({ boardId }) => ({
        url: `/boards/${boardId}/labels`,
        method: 'GET',
      }),
      transformResponse: (responseData) => {
        return labelsAdapter.setAll(initialCardState, responseData);
      },
    }),
    getTrelloBoardLists: build.query({
      query: ({ boardId }) => ({
        url: `/boards/${boardId}/lists`,
        method: 'GET',
      }),
      transformResponse: (responseData) => {
        return listsAdapter.setAll(initialCardState, responseData);
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTrelloBoardCardsQuery,
  useGetTrelloCardQuery,
  useGetTrelloBoardListsQuery,
  useGetTrelloBoardLabelsQuery,
} = cardsApi;

// {{{ fetchCardsByIds
export const fetchCardsByIds = async (
  dispatch,
  ids,
  filterCallback = () => true
) => {
  const deletedCardsIds = [];

  const outCards = (
    await Promise.all(
      ids.filter(filterCallback).map((cardId) =>
        dispatch(
          cardsApi.endpoints.getTrelloCard.initiate({
            cardId,
          })
        )
      )
    )
  ).reduce((acc, response) => {
    if (response.status === 'rejected') {
      deletedCardsIds.push(response.originalArgs.cardId);
      return acc;
    }
    return {
      ...acc,
      ...response.data.entities,
    };
  }, {});

  const deletedCards = !deletedCardsIds.length
    ? {}
    : (
        await chronosApi.getCardsArchive({
          body: {
            cardIds: deletedCardsIds,
          },
        })
      ).reduce((acc, response) => {
        acc[response.cardId] = response;
        return acc;
      }, {});

  const cards = {
    ...outCards,
    ...deletedCards,
  };
  return cards;
};
// }}}

export function useTrelloCards({ boardId, filter, fields }) {
  const outCardsList = useRef([]);
  const dispatch = useDispatch();

  const { data: { entities: cardsEntities } = initialCardState } =
    useGetTrelloBoardCardsQuery({
      boardId,
      filter,
      fields,
    });

  const fetchOutCardsDebounced = useDebouncedCallback(
    async () => {
      const outCardsIds = [...outCardsList.current];
      outCardsList.current = [];
      const filterCallback = (cardId) => !cardsEntities[cardId];
      const cards = await fetchCardsByIds(
        dispatch,
        outCardsIds,
        filterCallback
      );

      if (Object.keys(cards).length) {
        dispatch(
          cardsApi.util.updateQueryData(
            'getTrelloBoardCards',
            { boardId, filter, fields },
            (draft) => {
              cardsAdapter.setMany(draft, cards);
            }
          )
        );
      }
    },
    500,
    {
      leading: false,
    }
  );

  const fetchOutTrelloCards = useCallback((ids) => {
    const idsObject = ids.reduce((acc, id) => {
      acc[id] = id;
      return acc;
    }, {});
    const outCardsObject = outCardsList.current.reduce((acc, id) => {
      acc[id] = id;
      return acc;
    }, {});
    outCardsList.current = Object.keys({
      ...idsObject,
      ...outCardsObject,
    });

    fetchOutCardsDebounced();
  }, []);

  return [fetchOutTrelloCards];
}

export default cardsApi;
